/**
 * Prebid user-id configuration owned by the SDK (plans/ads-identity-unification.md,
 * owner decision 2026-09-09). Replaces the RebelMouse snippet's
 * `window.userIds`:
 *
 *  - `sharedId` keeps the `_sharedID` cookie name (existing cookies stay
 *    readable) but has **no `pixelUrl`** — the RebelMouse `/r/kappa/...` sync
 *    endpoint is no longer used; the cookie is set by Prebid in the browser.
 *  - `unifiedId` (The Trade Desk) is unchanged.
 *  - `uid2` is added for signed-in members (email-keyed, as before).
 *
 * Every entry exists only while the advertising decision is APPROVED; any
 * other state yields an empty list, which `configurePrebid` turns into "no id
 * module, no secure signal". The identity module's derived ads id is never
 * part of this list: it goes to GAM as PPID only, never to bidders.
 * @module lib/adIdentityConfig
 */
const TTD_PARTNER_ID = 'jo5wht2';
const UID2_SERVER_PUBLIC_KEY = 'UID2-X-P-MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAETb92MQDsrOcv/yTevOxi3dd2HUxCWieRx+R8gmvwBDxnVW1W12LDDZL1I+l+Ytt9oxkPU470iqU/lp6u5ODq9Q==';
const UID2_SUBSCRIPTION_ID = 'HDJUi7J2ub';

function defaultUserIds() {
  return [
    {
      name: 'sharedId',
      storage: { name: '_sharedID', type: 'cookie', expires: 365 }
    },
    {
      name: 'unifiedId',
      params: { url: `//match.adsrvr.org/track/rid?ttd_pid=${TTD_PARTNER_ID}&fmt=json` },
      storage: { type: 'cookie', name: 'pbjs-unifiedid', expires: 180 }
    }
  ];
}

function uid2Entry(email) {
  return {
    name: 'uid2',
    params: { serverPublicKey: UID2_SERVER_PUBLIC_KEY, subscriptionId: UID2_SUBSCRIPTION_ID, email }
  };
}

function stripForbidden(entries) {
  // Defensive: a page-level override may not reintroduce a sync pixel.
  return entries.map((entry) => {
    if (!entry || typeof entry !== 'object') return entry;
    if (entry.params && typeof entry.params === 'object' && 'pixelUrl' in entry.params) {
      const { pixelUrl, ...params } = entry.params;
      return { ...entry, params };
    }
    return entry;
  });
}

/**
 * @param {{ adsConsent: string, email?: string|null, explicit?: Array|null }} input
 * @returns {Array} Prebid `userSync.userIds` entries (empty when not approved)
 */
function buildUserIds({ adsConsent, email = null, explicit = null } = {}) {
  if (adsConsent !== 'APPROVED') return [];
  const base = stripForbidden(Array.isArray(explicit) ? explicit.slice() : defaultUserIds());
  if (typeof email === 'string' && email && !base.some((entry) => entry && entry.name === 'uid2')) {
    base.push(uid2Entry(email));
  }
  return base;
}

module.exports = {
  TTD_PARTNER_ID,
  UID2_SUBSCRIPTION_ID,
  buildUserIds,
  defaultUserIds,
  uid2Entry
};
